'use client'
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import Link from 'next/link'
import { ArrowRight, Calendar, Users, Zap, Bell, Mail, CheckCircle, Rocket, Shield, Star } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { AnimatedButtonLink } from '@/components/ui/animated-button-link'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import Image from 'next/image'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { LogoIcon } from '@/components/logo'
import { useMutation } from 'convex/react'
import { api } from 'socialloop-convex/api'
import { DotPattern } from '@/components/magicui/dot-pattern'
import { useAuth } from '@/contexts/auth-context'
import { useBetaAuth } from '@/hooks/use-beta-auth'
import confetti from 'canvas-confetti'

// Launch countdown target date (September 2, 2025)
const LAUNCH_DATE = new Date('2025-09-02T00:00:00Z')

interface TimeLeft {
    days: number
    hours: number
    minutes: number
    seconds: number
}

export default function ComingSoonPage() {
    const [timeLeft, setTimeLeft] = useState<TimeLeft>({
        days: 30,
        hours: 0,
        minutes: 0,
        seconds: 0
    })
    const [email, setEmail] = useState('')
    const [isSubscribed, setIsSubscribed] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [betaEmail, setBetaEmail] = useState('')
    const [betaPassword, setBetaPassword] = useState('')
    const [betaError, setBetaError] = useState('')
    const [hasBetaAccess, setHasBetaAccess] = useState(false)
    const subscribeNewsletter = useMutation(api.newsletter.subscribe)

    // Use simplified beta auth hook
    const { loading: isSigningIn, handleBetaLogin } = useBetaAuth({
        redirectTo: '/dashboard',
        onError: (error) => setBetaError(error)
    })

    const updateCountdown = useCallback(() => {
        const now = new Date().getTime()
        const distance = LAUNCH_DATE.getTime() - now

        if (distance > 0) {
            const days = Math.floor(distance / (1000 * 60 * 60 * 24))
            const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
            const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
            const seconds = Math.floor((distance % (1000 * 60)) / 1000)

            setTimeLeft(prev => {
                if (prev.days !== days || prev.hours !== hours || prev.minutes !== minutes || prev.seconds !== seconds) {
                    return { days, hours, minutes, seconds }
                }
                return prev
            })
        } else {
            setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 })
        }
    }, [])

    useEffect(() => {
        updateCountdown()
        const timer = setInterval(updateCountdown, 1000)
        return () => clearInterval(timer)
    }, [updateCountdown])

    const handleEmailSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!email) return

        setIsSubmitting(true)

        try {
            await subscribeNewsletter({ email: email.toLowerCase(), source: 'coming-soon' })
            setIsSubscribed(true)
            setEmail('')
            // Trigger confetti celebration
            confetti({
                particleCount: 100,
                spread: 70,
                origin: { y: 0.6 }
            })
        } catch (error) {
            // Still show success to prevent enumeration attacks
            setIsSubscribed(true)
            setEmail('')
            confetti({
                particleCount: 100,
                spread: 70,
                origin: { y: 0.6 }
            })
        }

        setIsSubmitting(false)
    }

    const handleBetaSignIn = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!betaEmail || !betaPassword) return

        setBetaError('')

        // Store beta access info for middleware
        localStorage.setItem('socialloop_beta_access', 'true')
        localStorage.setItem('socialloop_beta_email', betaEmail)

        // Use simplified beta auth hook
        await handleBetaLogin(betaEmail.toLowerCase(), betaPassword)
    }

    useEffect(() => {
        const hasAccess = localStorage.getItem('socialloop_beta_access') === 'true'
        if (hasAccess) {
            setHasBetaAccess(true)
            const savedEmail = localStorage.getItem('socialloop_beta_email')
            if (savedEmail) setBetaEmail(savedEmail)
        }
    }, [])


    const features = useMemo(() => [
        {
            icon: <Users className="w-6 h-6 text-white" />,
            title: "FUN ICEBREAKERS",
            description: "Break the ice with engaging games and conversation starters",
            color: "bg-blue-400"
        },
        {
            icon: <Zap className="w-6 h-6 text-white" />,
            title: "REAL-TIME ENGAGEMENT",
            description: "Live interactive games and instant networking opportunities",
            color: "bg-green-400"
        },
        {
            icon: <Calendar className="w-6 h-6 text-white" />,
            title: "EVENT MANAGEMENT",
            description: "Complete platform for hosting memorable networking events",
            color: "bg-purple-400"
        }
    ], [])

    const countdownItems = useMemo(() => [
        { label: 'DAYS', value: timeLeft.days, color: 'bg-yellow-400' },
        { label: 'HOURS', value: timeLeft.hours, color: 'bg-pink-400' },
        { label: 'MINUTES', value: timeLeft.minutes, color: 'bg-orange-400' },
        { label: 'SECONDS', value: timeLeft.seconds, color: 'bg-red-400' }
    ], [timeLeft.days, timeLeft.hours, timeLeft.minutes, timeLeft.seconds])

    return (
        <div className="coming-soon-container relative min-h-screen w-full bg-white">
            {/* Scattered Decorative Elements (hidden on mobile) */}
            <div className="hidden sm:block absolute top-20 left-10 w-8 h-8 bg-pink-400 border-2 border-black transform rotate-45 shadow-[3px_3px_0px_0px_#000000] z-10"></div>
            <div className="hidden sm:block absolute top-40 right-20 w-6 h-6 bg-yellow-400 border-2 border-black rounded-full shadow-[3px_3px_0px_0px_#000000] z-10"></div>
            <div className="hidden sm:block absolute top-60 left-1/4 w-0 h-0 border-l-[15px] border-r-[15px] border-b-[20px] border-l-transparent border-r-transparent border-b-green-400 shadow-[3px_3px_0px_0px_#000000] z-10"></div>
            <div className="hidden sm:block absolute top-80 right-1/3 w-10 h-6 bg-blue-400 border-2 border-black transform -rotate-12 shadow-[3px_3px_0px_0px_#000000] z-10"></div>
            <div className="hidden sm:block absolute top-96 left-16 w-5 h-5 bg-red-400 border-2 border-black transform rotate-45 shadow-[3px_3px_0px_0px_#000000] z-10"></div>
            <div className="hidden sm:block absolute bottom-40 right-12 w-7 h-7 bg-purple-400 border-2 border-black rounded-full shadow-[3px_3px_0px_0px_#000000] z-10"></div>
            <div className="hidden sm:block absolute bottom-60 left-1/3 w-12 h-4 bg-orange-400 border-2 border-black transform rotate-6 shadow-[3px_3px_0px_0px_#000000] z-10"></div>
            <div className="hidden sm:block absolute bottom-80 right-1/4 w-0 h-0 border-l-[12px] border-r-[12px] border-b-[16px] border-l-transparent border-r-transparent border-b-cyan-400 shadow-[3px_3px_0px_0px_#000000] z-10"></div>

            {/* Optimized Dot Pattern Background */}
            <DotPattern
                width={24}
                height={24}
                cx={1}
                cy={1}
                cr={1}
                glow={false}
                className="fill-neutral-400/15 [mask-image:radial-gradient(800px_circle_at_center,white,transparent)]"
            />

            {/* Main Content */}
            <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
                <div className="w-full max-w-6xl mx-auto">
                    
                    {/* Header Section */}
                    <div className="text-center mb-8">
                        {/* Logo */}
                        <div className="mb-6">
                            <Link href="/" aria-label="go home" className="inline-block">
                                <div className="bg-yellow-400 border-4 border-black px-4 py-3 transform -rotate-2 hover:rotate-0 transition-transform duration-200">
                                    <LogoIcon height={40}/>
                                </div>
                            </Link>
                        </div>

                        {/* Status Badge with Beta Access */}
                        <div className="mb-8 relative">
                            {/* Sticky Note Testimonial (hidden on mobile) */}
                            <div className="hidden sm:block absolute top-2 right-2 bg-yellow-300 border-2 border-black p-3 transform rotate-12 shadow-[4px_4px_0px_0px_#000000] z-20">
                                <p className="text-xs font-bold text-black">"Finally! 🎉"</p>
                                <div className="text-[8px] text-black/70 mt-1">- Beta Tester</div>
                            </div>

                            <div className="bg-red-400 border-4 border-black p-4 transform rotate-1 hover:rotate-0 transition-all duration-200 inline-block shadow-[6px_6px_0px_0px_#000000] hover:shadow-[2px_2px_0px_0px_#000000] hover:translate-x-1 hover:translate-y-1" style={{willChange: 'transform'}}>
                                <div className="flex items-center gap-3">
                                    <div className="bg-black p-2 border-2 border-black">
                                        <Rocket className="w-5 h-5 text-white" />
                                    </div>
                                    <div className="text-left">
                                        <h2 className="text-lg font-black text-black">COMING SOON!</h2>
                                        <p className="text-sm font-bold text-black">THE FUTURE IS ALMOST HERE</p>
                                    </div>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <div className="bg-black border-4 border-black rounded px-4 py-2 cursor-pointer hover:bg-white hover:text-black transition-all duration-200 transform rotate-2 hover:rotate-0">
                                                <span className="text-white font-black text-xs hover:text-black">
                                                    {hasBetaAccess ? "BETA ACTIVE" : "BETA ACCESS"}
                                                </span>
                                            </div>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-80 border-4 border-black bg-white" align="center">
                                            {hasBetaAccess ? (
                                                <div className="space-y-4">
                                                    <div className="bg-green-400 border-4 border-black p-4">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <CheckCircle className="w-6 h-6 text-black" />
                                                            <span className="text-black font-black text-lg">BETA ACTIVE!</span>
                                                        </div>
                                                        <p className="text-black font-bold text-sm">
                                                            Welcome to SocialLoop Beta! You have early access to all features.
                                                        </p>
                                                    </div>
                                                    <div className="space-y-3">
                                                        <Link
                                                            href="/"
                                                            className="w-full bg-black border-4 border-black rounded text-white px-8 py-4 font-black text-lg uppercase tracking-wider hover:bg-white hover:text-black transition-all duration-200 block text-center transform rotate-2 hover:rotate-0"
                                                        >
                                                            ACCESS BETA FEATURES
                                                        </Link>
                                                        <Button 
                                                            variant="outline" 
                                                            className="w-full border-4 border-black rounded bg-blue-400 text-black px-6 py-3 font-bold hover:bg-black hover:text-white transform rotate(-1deg) hover:rotate-0 transition-all duration-200"
                                                            onClick={() => {
                                                                localStorage.removeItem('socialloop_beta_access')
                                                                localStorage.removeItem('socialloop_beta_email')
                                                                setHasBetaAccess(false)
                                                                setBetaEmail('')
                                                                setBetaPassword('')
                                                            }}
                                                        >
                                                            SIGN OUT
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="space-y-4">
                                                    <div className="bg-blue-400 border-4 border-black p-4">
                                                        <h3 className="text-xl font-black text-black mb-2">BETA ACCESS LOGIN</h3>
                                                        <p className="text-black font-bold text-sm">
                                                            Enter your credentials to access SocialLoop beta features
                                                        </p>
                                                    </div>
                                                    <form onSubmit={handleBetaSignIn} className="space-y-4">
                                                        <div>
                                                            <Label htmlFor="beta-email" className="block text-lg font-black text-black mb-2">
                                                                EMAIL
                                                            </Label>
                                                            <Input
                                                                id="beta-email"
                                                                type="email"
                                                                placeholder="enter@email.com"
                                                                value={betaEmail}
                                                                onChange={(e) => setBetaEmail(e.target.value)}
                                                                className="border-4 border-black bg-white font-bold text-black placeholder:text-black/60 focus:bg-yellow-400 focus:border-8 transition-all duration-200"
                                                                required
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label htmlFor="beta-password" className="block text-lg font-black text-black mb-2">
                                                                PASSWORD
                                                            </Label>
                                                            <Input
                                                                id="beta-password"
                                                                type="password"
                                                                placeholder="Enter beta password"
                                                                value={betaPassword}
                                                                onChange={(e) => setBetaPassword(e.target.value)}
                                                                className="border-4 border-black bg-white font-bold text-black placeholder:text-black/60 focus:bg-yellow-400 focus:border-8 transition-all duration-200"
                                                                required
                                                            />
                                                        </div>
                                                        {betaError && (
                                                            <div className="bg-red-400 border-4 border-black p-3">
                                                                <p className="text-black font-black text-sm">{betaError}</p>
                                                            </div>
                                                        )}
                                                        <Button 
                                                            type="submit" 
                                                            className="w-full bg-black text-white font-black border-4 border-black rounded px-8 py-4 text-lg uppercase tracking-wider hover:bg-white hover:text-black transition-all duration-200 transform rotate-2 hover:rotate-0"
                                                            disabled={isSigningIn}
                                                        >
                                                            {isSigningIn ? 'SIGNING IN...' : 'ACCESS BETA'}
                                                        </Button>
                                                    </form>
                                                    <div className="text-center">
                                                        <p className="text-black font-bold text-sm">
                                                            Need beta access?{' '}
                                                            <Link href="mailto:hello@socialloop.ai" className="underline hover:no-underline">
                                                                Contact us
                                                            </Link>
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </PopoverContent>
                                    </Popover>
                                </div>
                            </div>
                        </div>

                        {/* Main Heading */}
                        <div className="space-y-4 mb-8">
                            <div className="bg-blue-400 border-4 border-black p-4 sm:p-6 transform -rotate-1 hover:rotate-0 transition-all duration-200 shadow-[6px_6px_0px_0px_#000000] hover:shadow-[2px_2px_0px_0px_#000000] hover:translate-x-1 hover:translate-y-1">
                                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-black leading-tight">
                                    THE FUTURE OF EVENT<br />
                                    NETWORKING IS ALMOST HERE!
                                </h1>
                            </div>

                            <div className="bg-green-400 border-4 border-black p-3 sm:p-4 transform rotate-1 hover:rotate-0 transition-all duration-200 shadow-[6px_6px_0px_0px_#000000] hover:shadow-[2px_2px_0px_0px_#000000] hover:translate-x-1 hover:translate-y-1">
                                <p className="text-sm sm:text-base md:text-lg font-bold text-black">
                                    WE'RE PUTTING THE FINISHING TOUCHES ON SOMETHING REVOLUTIONARY!
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Countdown Timer */}
                    <div className="mb-8">
                        <div className="bg-purple-400 border-4 border-black p-4 sm:p-6 transform -rotate-1 hover:rotate-0 transition-all duration-200 mb-6 shadow-[6px_6px_0px_0px_#000000] hover:shadow-[2px_2px_0px_0px_#000000] hover:translate-x-1 hover:translate-y-1">
                            <h2 className="text-xl sm:text-2xl font-black text-black text-center mb-4">LAUNCHING IN:</h2>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {countdownItems.map((item, index) => (
                                    <div
                                        key={item.label}
                                        className={`${item.color} border-4 border-black p-3 sm:p-4 text-center transform ${index % 2 === 0 ? 'rotate-2' : '-rotate-2'} shadow-[4px_4px_0px_0px_#000000]`}
                                    >
                                        <div className="text-2xl sm:text-3xl md:text-4xl font-black text-black">
                                            {item.value.toString().padStart(2, '0')}
                                        </div>
                                        <div className="text-xs sm:text-sm font-black text-black mt-1">
                                            {item.label}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Email Signup */}
                    <div className="mb-8 max-w-xl mx-auto">
                        {!isSubscribed ? (
                            <div className="bg-orange-400 border-4 border-black p-4 sm:p-6 transform rotate-1 hover:rotate-0 transition-all duration-200 shadow-[6px_6px_0px_0px_#000000] hover:shadow-[2px_2px_0px_0px_#000000] hover:translate-x-1 hover:translate-y-1 relative">
                                {/* Hand-drawn Arrow */}
                                <div className="absolute -top-12 -right-8 hidden sm:block">
                                    <svg width="60" height="40" viewBox="0 0 60 40" className="text-black">
                                        <path d="M10 30 Q30 10 50 20" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round"/>
                                        <path d="M45 15 L50 20 L45 25" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round"/>
                                    </svg>
                                    <div className="text-xs font-bold text-black transform -rotate-12 mt-1">Join now!</div>
                                </div>

                                <h3 className="text-lg sm:text-xl font-black text-black text-center mb-4">
                                    GET EARLY ACCESS!
                                </h3>
                                <form onSubmit={handleEmailSubmit} className="space-y-3">
                                    <div className="relative">
                                        <Input
                                            type="email"
                                            placeholder="YOUR@EMAIL.COM"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="rounded border-4 border-black bg-white font-bold text-black placeholder:text-black/60 placeholder:font-bold focus:bg-yellow-400 focus:border-4 text-base sm:text-lg px-4 py-4 min-h-[56px] transition-all duration-200"
                                            required
                                        />
                                    </div>
                                    <Button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full bg-black text-white font-black text-sm sm:text-lg px-4 sm:px-8 py-3 sm:py-4 border-4 border-black rounded uppercase tracking-wide sm:tracking-wider hover:bg-white hover:text-black transition-all duration-200 transform rotate-2 hover:rotate-0"
                                    >
                                        {isSubmitting ? (
                                            <span className="text-xs sm:text-sm">ADDING YOU TO THE LIST...</span>
                                        ) : (
                                            <>
                                                <Bell className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2 flex-shrink-0" />
                                                <span className="text-xs sm:text-base leading-tight">
                                                    GET NOTIFIED WHEN WE LAUNCH!
                                                </span>
                                            </>
                                        )}
                                    </Button>
                                </form>
                            </div>
                        ) : (
                            <div className="bg-green-400 border-4 border-black p-4 sm:p-6 text-center transform -rotate-1 hover:rotate-0 transition-all duration-200 shadow-[6px_6px_0px_0px_#000000] hover:shadow-[2px_2px_0px_0px_#000000] hover:translate-x-1 hover:translate-y-1">
                                <div className="flex items-center justify-center gap-3">
                                    <CheckCircle className="w-8 h-8 text-black" />
                                    <div>
                                        <h3 className="text-lg sm:text-xl font-black text-black">YOU'RE ON THE LIST!</h3>
                                        <p className="text-sm sm:text-base font-bold text-black">WE'LL NOTIFY YOU WHEN WE LAUNCH!</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Torn Paper Divider */}
                    <div className="w-full h-6 bg-black mb-8" style={{
                        clipPath: 'polygon(0 0, 100% 0, 98% 50%, 100% 100%, 0 100%, 2% 50%)'
                    }}></div>

                    {/* Feature Preview */}
                    <div className="mb-8 relative">
                        {/* Sticky Note Callout (hidden on mobile) */}
                        <div className="hidden sm:block absolute -top-4 -left-4 bg-green-300 border-2 border-black p-2 transform -rotate-6 shadow-[3px_3px_0px_0px_#000000] z-20">
                            <p className="text-xs font-bold text-black">NEW!</p>
                        </div>

                        <div className="bg-pink-400 border-4 border-black p-4 sm:p-6 transform rotate-1 hover:rotate-0 transition-all duration-200 mb-6 shadow-[6px_6px_0px_0px_#000000] hover:shadow-[2px_2px_0px_0px_#000000] hover:translate-x-1 hover:translate-y-1">
                            <h2 className="text-2xl sm:text-3xl font-black text-black text-center">WHAT'S COMING</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                            {features.map((feature, index) => (
                                <div
                                    key={feature.title}
                                    className={`${feature.color} border-4 border-black p-4 transform ${index % 2 === 0 ? '-rotate-1' : 'rotate-1'} hover:rotate-0 transition-all duration-200 cursor-pointer shadow-[4px_4px_0px_0px_#000000] hover:shadow-[1px_1px_0px_0px_#000000] hover:translate-x-1 hover:translate-y-1`}
                                >
                                    <div className="bg-black p-3 border-2 border-black mb-3 inline-block">
                                        {feature.icon}
                                    </div>
                                    <h3 className="text-base sm:text-lg font-black text-black mb-2">{feature.title}</h3>
                                    <p className="font-bold text-black text-sm leading-tight">{feature.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Zigzag Divider */}
                    <div className="w-full h-4 bg-purple-400 mb-8" style={{
                        clipPath: 'polygon(0 0, 5% 100%, 10% 0, 15% 100%, 20% 0, 25% 100%, 30% 0, 35% 100%, 40% 0, 45% 100%, 50% 0, 55% 100%, 60% 0, 65% 100%, 70% 0, 75% 100%, 80% 0, 85% 100%, 90% 0, 95% 100%, 100% 0, 100% 100%, 0 100%)'
                    }}></div>

                    {/* Trust Indicators */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto relative">
                        {/* Sticky Note Testimonial (hidden on mobile) */}
                        <div className="hidden sm:block absolute bottom-6 -right-2 bg-pink-300 border-2 border-black p-3 transform rotate-6 shadow-[4px_4px_0px_0px_#000000] z-20">
                            <p className="text-xs font-bold text-black">"Can't wait! 🚀"</p>
                            <div className="text-[8px] text-black/70 mt-1">- Early User</div>
                        </div>

                        <div className="bg-red-400 border-4 border-black p-4 text-center transform -rotate-1 shadow-[4px_4px_0px_0px_#000000]">
                            <div className="bg-black p-2 border-2 border-black inline-block mb-3">
                                <Shield className="w-6 h-6 text-white" />
                            </div>
                            <div className="text-2xl sm:text-3xl font-black text-black">100%</div>
                            <div className="text-xs sm:text-sm font-black text-black">SECURE & PRIVATE</div>
                        </div>
                        <div className="bg-blue-400 border-4 border-black p-4 text-center transform rotate-1 shadow-[4px_4px_0px_0px_#000000]">
                            <div className="bg-black p-2 border-2 border-black inline-block mb-3">
                                <Star className="w-6 h-6 text-white" />
                            </div>
                            <div className="text-2xl sm:text-3xl font-black text-black">PREMIUM</div>
                            <div className="text-xs sm:text-sm font-black text-black">NETWORKING EXPERIENCE</div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    )
} 